import { Burden, Callback2, VoidCallback2 } from '@deqo/foundation';
import React, { useEffect, useMemo, useState } from 'react';
import { Component, ObjectManager, ObjectSymbols } from '../../../concepts/objects';
import { ContainerScope, resolve, useResolve } from '../../Container';

const { Ticker } = ObjectSymbols;

export interface ComponentObserverProps {
    object: Component;
    callback?: VoidCallback2<Component, number>;
    render?: Callback2<React.ReactElement<any>, Component, number>;
}

export class ComponentObserver extends React.Component<ComponentObserverProps> {

    public static contextType = ContainerScope;

    @resolve(ObjectManager) objects!: ObjectManager;

    public state = { ticker: -1, };

    private burdens: Burden[] = [];

    public render() {
        return !!this.props.render ? this.props.render(this.props.object, this.state.ticker) : null;
    }

    public componentDidMount(): void {
        this.componentDidUpdate();
    }

    public componentDidUpdate() {
        const { callback, render } = this.props;
        this.componentWillUnmount();
        this.burdens = [];
        if (callback) {
            this.burdens.push(this.createSink(callback));
        }
        if (render) {
            this.burdens.push(this.createSink((_, ticker) => this.setState({ ticker })));
        }
    }

    public componentWillUnmount(): void {
        Burden.resolveAll(this.burdens);
    }

    private createSink(callback: VoidCallback2<Component, number>) {
        return this.objects.on(this.props.object.id, 'affected', ([o]) => {
            callback(o, o[Ticker]);
        });
    }
}

export const useComponentObserver = <T extends Component | Component[]>(input: T) => {
    const objects = useResolve(ObjectManager);
    const [tick, setTick] = useState(0);

    const subjects = useMemo(() => Array.isArray(input) ? input : [input], [input]);
    const ids = useMemo(() => new Set(subjects.filter(x => !!x).map(x => x.id)), [subjects]);

    useEffect(() => {
        return objects.on('updated', updated => {
            if (updated.find(x => ids.has(x.id))) {
                setTick(t => t + 1);
            }
        });
    }, [ids]);

    return tick;
};

export const useComponentAffected = (c: Component) => {

    const objects = useResolve(ObjectManager);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        return objects.on(c.id, 'affected', () => {
            setTick(t => t + 1);
        });
    }, [c]);

    return tick;
};
